import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AddInventoryDialog from './Popups/AddInventory'
import AddEquipmentDialog from './Popups/AddEquipment'
import AddMachineDialog from './Popups/AddMachine'
import MachineImport from './MachineImport'
import MachineExport from './MachineExport'
import EquipmentImport from './EquipmentImport'
import EquipmentExport from './EquipmentExport'
import InventoryImport from './InventoryImport'
import InventoryExport from './InventoryExport'
import AddWorkbenchDialog from './Popups/AddWorkbench' // Dummy Component
import BarcodeScanner from './Barcode/BarcodeScanner'
import { useSelector } from 'react-redux'
import MachineListAPIs from '../../utilities/api/machine/AdminMachineList'
import EquipmentListAPIs from '../../utilities/api/equipment/AdminEquipmentList'
import InventoryListAPIs from '../../utilities/api/inventory/AdminInventoryList'

const TopBarComponent = ({
  activeTab,
  setActiveTab,
  update,
  setUpdate,
  flag,
  setFlag,
  token,
  groups
}) => {
  const user = useSelector((state) => state.user.user)
  const categories = useSelector((state) => state.category.category_list)
  const locations = useSelector((state) => state.location.location_list)
  const userToken = user.access

  const [isAddInventoryOpen, setAddInventoryOpen] = useState(false)
  const [isAddEquipmentOpen, setAddEquipmentOpen] = useState(false)
  const [isAddMachineOpen, setAddMachineOpen] = useState(false)
  const [isAddWorkbenchOpen, setAddWorkbenchOpen] = useState(false)
  const [isImportMachineOpen, setImportMachineOpen] = useState(false)
  const [isImportEquipmentOpen, setImportEquipmentOpen] = useState(false)
  const [isImportInventoryOpen, setImportInventoryOpen] = useState(false)

  const [isScannerOpen, setScannerOpen] = useState(false) // State to toggle the barcode scanner dialog
  const [machines, setMachines] = useState([])
  const [equipments, setEquipments] = useState([])
  const [inventories, setInventories] = useState([])

  useEffect(() => {
    const fetchExportData = async () => {
      const apiToken = token ? token : userToken
      const [machinesResponse, equipmentsResponse, inventoriesResponse] = await Promise.all([
        MachineListAPIs.MachineListGet(),
        EquipmentListAPIs.EquipmentListGet(),
        InventoryListAPIs.InventoryListGet(apiToken)
      ])
      setMachines(machinesResponse)
      setEquipments(equipmentsResponse)
      setInventories(inventoriesResponse)
    }

    fetchExportData()
  }, [])

  const handleAddButtonClick = () => {
    switch (activeTab) {
      case 0:
        setAddMachineOpen(true)
        break
      case 1:
        setAddEquipmentOpen(true)
        break
      case 2:
        setAddInventoryOpen(true)
        break
      case 3: // Handle Workbench case
        setAddWorkbenchOpen(true) // Open the Add Workbench dialog
        break
      default:
        break
    }
  }

  const handleClose = () => {
    switch (activeTab) {
      case 0:
        setAddMachineOpen(false)
        break
      case 1:
        setAddEquipmentOpen(false)
        break
      case 2:
        setAddInventoryOpen(false)
        break
      case 3: // For Workbench
        setAddWorkbenchOpen(false) // Close the Add Workbench dialog
        break
      default:
        break
    }
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleImportClick = () => {
    switch (activeTab) {
      case 0:
        setImportMachineOpen(true)
        break
      case 1:
        setImportEquipmentOpen(true)
        break
      case 2:
        setImportInventoryOpen(true)
        break
      default:
        alert('This feature is not available at the moment')
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: isMobile ? 1 : 0,
        padding: theme.spacing(1),
        margin: 'none',
        marginBlock: '1rem',
        boxSizing: 'border-box'
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleChange}
        indicatorColor="none"
        sx={{
          padding: '8px',
          width: 'auto',
          marginBottom: isMobile ? 1 : 0,
          height: 'auto',
          background: '#F4F4F4',
          borderRadius: '12px',
          '& .MuiTab-root': {
            color: '#333333 !important',
            background: '#fff !important',
            border: '1px solid #E6E6E6 !important',
            borderRadius: '8px',
            marginInline: 0.5,
            padding: '8px 24px',
            width: 'auto',
            typography: 'body1'
          },
          '& .Mui-selected': {
            color: '#fff !important',
            background: '#333333 !important',
            border: '1px solid #333333 !important',
            borderRadius: '8px'
          }
        }}
      >
        <Tooltip
          title="Machine contains items which are fixed in TL and can be reserved by booking a free slot"
          arrow
        >
          <Tab label="Machines" />
        </Tooltip>
        <Tooltip
          title="Equipments contain items which are fixed in TL and do not require approval or reservations"
          arrow
        >
          <Tab label="Equipment" />
        </Tooltip>
        <Tooltip
          title="Inventory contains all items in the TL that can be shelved (inventory that is not fixed like machines or equipment)"
          arrow
        >
          <Tab label="Inventory" />
        </Tooltip>
      </Tabs>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          gap: 2
        }}
      >
        {/* Import components based on activeTab */}
        {activeTab === 0 && (
          <>
            <MachineExport
              machines={machines} // This will be populated with actual machine data
              categories={categories}
              locations={locations}
            />
            <MachineImport
              user={user}
              categories={categories}
              locations={locations}
              onImportComplete={() => {
                setUpdate((prev) => !prev)
                setFlag((prev) => !prev)
              }}
            />
          </>
        )}
        {activeTab === 1 && (
          <>
            <EquipmentExport
              equipments={equipments} // This will be populated with actual equipment data
              categories={categories}
              locations={locations}
            />
            <EquipmentImport
              user={user}
              categories={categories}
              locations={locations}
              onImportComplete={() => {
                setUpdate((prev) => !prev)
                setFlag((prev) => !prev)
              }}
            />
          </>
        )}
        {activeTab === 2 && (
          <>
            <InventoryExport
              inventories={inventories} // This will be populated with actual inventory data
              categories={categories}
              locations={locations}
            />
            <InventoryImport
              user={user}
              categories={categories}
              locations={locations}
              groups={groups}
              onImportComplete={() => {
                setUpdate((prev) => !prev)
                setFlag((prev) => !prev)
              }}
            />
          </>
        )}

        {/* Add Button */}
        <Button
          variant="contained"
          onClick={handleAddButtonClick}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: '12px 16px',
            background: '#007BFF',
            borderRadius: '12px',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#007BFF' }
          }}
        >
          <AddIcon />
          {activeTab === 0 ? (
            <Typography variant="subtitle1">Add Machine</Typography>
          ) : activeTab === 1 ? (
            <Typography variant="subtitle1">Add Equipment</Typography>
          ) : activeTab === 2 ? (
            <Typography variant="subtitle1">Add Inventory</Typography>
          ) : (
            <Typography variant="subtitle1">Add Workbench</Typography>
          )}
        </Button>
      </Box>

      {/* Barcode Scanner Dialog */}
      <Dialog open={isScannerOpen} onClose={() => setScannerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Scan a Barcode</DialogTitle>
        <DialogContent>
          <BarcodeScanner onClose={() => setScannerOpen(false)} />{' '}
          {/* Pass onClose prop to close the dialog */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScannerOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogs for adding items */}
      <AddInventoryDialog
        open={isAddInventoryOpen}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
        onClose={() => {
          handleClose(activeTab)
        }}
        setScannerOpen={setScannerOpen}
      />
      <AddEquipmentDialog
        open={isAddEquipmentOpen}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
        onClose={() => {
          handleClose(activeTab)
        }}
        setScannerOpen={setScannerOpen}
      />
      <AddMachineDialog
        open={isAddMachineOpen}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
        onClose={() => {
          handleClose(activeTab)
        }}
        setScannerOpen={setScannerOpen}
      />
      <AddWorkbenchDialog
        open={isAddWorkbenchOpen}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
        onClose={() => {
          handleClose(activeTab)
        }}
      />

      {/* Import Dialogs */}
      {isImportMachineOpen && (
        <Dialog
          open={isImportMachineOpen}
          onClose={() => setImportMachineOpen(false)}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: { borderRadius: '10px' }
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <MachineImport
              user={user}
              categories={categories}
              locations={locations}
              onImportComplete={() => {
                setUpdate((prev) => !prev)
                setImportMachineOpen(false)
                setFlag((prev) => !prev)
              }}
            />
          </DialogContent>
        </Dialog>
      )}
      {isImportEquipmentOpen && (
        <Dialog
          open={isImportEquipmentOpen}
          onClose={() => setImportEquipmentOpen(false)}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: { borderRadius: '10px' }
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <EquipmentImport
              user={user}
              categories={categories}
              locations={locations}
              onImportComplete={() => {
                setUpdate((prev) => !prev)
                setImportEquipmentOpen(false)
                setFlag((prev) => !prev)
              }}
            />
          </DialogContent>
        </Dialog>
      )}
      {isImportInventoryOpen && (
        <Dialog
          open={isImportInventoryOpen}
          onClose={() => setImportInventoryOpen(false)}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: { borderRadius: '10px' }
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <InventoryImport
              user={user}
              categories={categories}
              locations={locations}
              groups={groups}
              onImportComplete={() => {
                setUpdate((prev) => !prev)
                setImportInventoryOpen(false)
                setFlag((prev) => !prev)
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  )
}

export default TopBarComponent
